// Dropdown - closes open [data-dropdown] targets on outside click and anchors
// the open menu to its trigger area. Behavioral port of v1 modules/Dropdown.js;
// relies on Toggle for the open/close mechanics.
//
// v1 positioned menus purely in CSS (`left: 0; top: 100%`) and only flipped
// horizontally by toggling .dropdown--align-right. v2 hands the open menu to
// @floating-ui/dom (same helper the tooltip uses), which keeps the v1 anchor
// points but adds vertical flip and viewport shift. Placement is derived from
// the v1 class contract, so markup and CSS-only fallback stay valid.

import { TOGGLE_EVT, doToggle, type ToggleEventDetail } from './toggle';
import {
  attachFloating,
  clearFloating,
  positionFloating,
  type FloatingOptions,
  type Placement,
} from './floating';

export interface DropdownConfig {
  dataInteractive: string;
  activeClassName: string;
  /** Gap between the anchor and the menu, px. v1 had the menu flush (0). */
  offset: number;
  /** Gap between the arrowhead variant and the anchor (v1: margin-top 10px). */
  arrowheadOffset: number;
  /** Viewport padding kept free by shift(), px. */
  padding: number;
}

const defaultConfig: DropdownConfig = {
  dataInteractive: 'data-dropdown-interactive',
  activeClassName: 'is-active',
  offset: 0,
  arrowheadOffset: 10,
  padding: 8,
};

const isActive = (el: Element): boolean =>
  el.classList.contains(defaultConfig.activeClassName)
  || el.getAttribute('aria-expanded') === 'true';

export default class Dropdown {
  selector: string;

  config: DropdownConfig;

  dropdowns: Element[] = [];

  target: Element | null = null;

  trigger: Element | null = null;

  /** Open menus → their floating-ui cleanup callback. */
  private anchored = new Map<Element, () => void>();

  constructor(selector = '[data-dropdown]', config: Partial<DropdownConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  destroy(): void {
    this.dropdowns.forEach(dropdown => {
      dropdown.removeEventListener(TOGGLE_EVT, this._onToggle);
      this._unanchor(dropdown);
    });

    this._removeListeners();

    this.dropdowns = [];
  }

  update(): void {
    this.destroy();
    this._init();
  }

  /**
   * Position a menu once against its anchor. Menus are re-positioned
   * automatically while open, so this is only needed for one-off cases
   * (a menu rendered open by the server, a manual DOM move).
   */
  static setDropdownPosition(target: Element): void {
    if (!(target instanceof HTMLElement)) return;

    const reference = Dropdown.getAnchor(target);
    if (!reference) return;

    void positionFloating(reference, target, Dropdown.floatingOptions(target, defaultConfig));
  }

  /**
   * Element the menu is anchored to. Defaults to the parent - v1's CSS
   * anchored the menu to its containing block, which is that wrapper
   * (`.btn-dropdown` and friends). `data-dropdown-anchor` overrides it with a
   * selector, e.g. to anchor to the trigger inside a wider wrapper.
   */
  static getAnchor(menu: HTMLElement): Element | null {
    const selector = menu.dataset.dropdownAnchor;

    return selector ? document.querySelector(selector) : menu.parentElement;
  }

  /** Placement + offset derived from the v1 class contract. */
  static floatingOptions(menu: HTMLElement, config: DropdownConfig): FloatingOptions {
    const arrowhead = menu.classList.contains('dropdown--arrowhead');

    let placement: Placement = 'bottom-start';
    if (arrowhead) {
      // v1: left: 50% + translateX(-50%) - centered under the anchor.
      placement = 'bottom';
    } else if (menu.classList.contains('dropdown--align-right')) {
      // v1: left: auto; right: 0 - flush with the anchor's right edge.
      placement = 'bottom-end';
    }

    return {
      placement: (menu.dataset.dropdownPlacement as Placement | undefined) ?? placement,
      offset: arrowhead ? config.arrowheadOffset : config.offset,
      padding: config.padding,
    };
  }

  private _init(): void {
    this.dropdowns.push(...document.querySelectorAll(this.selector));

    this.dropdowns.forEach(dropdown => {
      dropdown.addEventListener(TOGGLE_EVT, this._onToggle);

      // Menus rendered open (server-side aria-expanded) need positioning now;
      // closed ones are positioned when they open.
      if (isActive(dropdown)) {
        this._anchor(dropdown);
      }
    });

    this._addListeners();
  }

  /** Keep the menu anchored while it is open. */
  private _anchor(menu: Element): void {
    if (!(menu instanceof HTMLElement)) return;

    const reference = Dropdown.getAnchor(menu);
    if (!reference) return;

    this._unanchor(menu);

    const options = Dropdown.floatingOptions(menu, this.config);
    const detachFloating = attachFloating(reference, menu, options);

    // The menu animates open with a scaleY transform, so its measured height is
    // still growing when floating-ui first computes the position - re-run once
    // the transition lands so a flipped (top) placement sits correctly.
    const reposition = (): void => {
      void positionFloating(reference, menu, options);
    };
    menu.addEventListener('transitionend', reposition);

    this.anchored.set(menu, () => {
      detachFloating();
      menu.removeEventListener('transitionend', reposition);
    });
  }

  /** Stop tracking a menu and hand positioning back to the stylesheet. */
  private _unanchor(menu: Element): void {
    this.anchored.get(menu)?.();
    this.anchored.delete(menu);

    if (menu instanceof HTMLElement) clearFloating(menu);
  }

  private _onToggle = (e: Event): void => {
    const { detail } = e as CustomEvent<ToggleEventDetail>;

    if ((detail.trigger && !this.target) || !this.target?.contains(detail.target)) {
      this.trigger = detail.trigger ?? null;
    }
    if (!this.target || !this.target.contains(detail.target)) {
      this.target = detail.target;
    }

    // attach events only if dropdown is open
    if (this.target && isActive(this.target)) {
      this._anchor(this.target);

      this._addListeners();
    } else if (detail.target) {
      this._unanchor(detail.target);
    }
  };

  private _onClick = (e: Event): void => {
    if (!this.trigger || !this.target) {
      return;
    }
    const clicked = e.target as Node;

    if (this.trigger !== clicked && !this.trigger.contains(clicked)) {
      if (
        !this.target.hasAttribute(this.config.dataInteractive)
        || !this.target.contains(clicked)
      ) {
        doToggle({
          target: this.trigger,
          expand: true,
          state: false,
          icon: this.trigger.getAttribute('data-toggle-icon'),
          className: this.config.activeClassName,
        });
        doToggle({
          target: this.target,
          expand: true,
          state: false,
          className: this.config.activeClassName,
        });

        this._removeListeners();
      }
    } else {
      this._removeListeners();
    }
  };

  private _addListeners(): void {
    document.addEventListener('click', this._onClick, true);
  }

  private _removeListeners(): void {
    document.removeEventListener('click', this._onClick, true);
  }
}
